import { useRef, useState } from "react";
import {
  Button,
  DialogTextContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Icon,
  Flex,
  Search,
  Menu,
  ToolIcon,
  Badge
} from "@theorgchartdev/storybook";

import cx from "classnames";
import CentralizedModalContainer from "../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { useReportingContext } from "@/providers/ReportsProvider";
import { ReportsTable } from "@/appComponents/Blocks/Modals/ReportExplorer/ReportsTable";
import { GetWSMgr } from "@/Utils/StateLib";
import { DoReportRFC } from "@/Utils/Promises";
import SectionPrompt from "@/appComponents/Blocks/Composites/SectionPrompt/SectionPrompt";
import {
  GetNameValidation,
  enumNameValidation,
  GetNameValidationMessage
} from "@coreapp/utils/ValidateRename";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { Globals } from "@coreapp/CoreApp/Globals";

const ReportExplorer = props => {
  const {
    isOpenReportDirty,
    SaveReportChanges,
    showReportExplorer,
    closeReportExplorer,
    OpenReport,
    CreateReport,
    reports,
    setReports,
    currentOpenReport,
    UpdateOpenReport
  } = useReportingContext();

  const [selectedReport, setSelectedReport] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [filterChartIds, setFilterChartIds] = useState([]);

  const openCtrlRef = useRef(null);

  if (!showReportExplorer) return;

  const PromptToSave = onProceed => {
    const wsMgr = GetWSMgr();
    wsMgr?.CreateReactAlert(
      appstr("saveandcontinue"),
      appstr("genericsave"),
      async () => {
        await SaveReportChanges();
        onProceed();
      },
      onProceed,
      appstr("Save Changes"),
      appstr("Discard Changes")
    );
  };

  const HandleCreateReport = () => {
    if (isOpenReportDirty) {
      PromptToSave(() => {
        CreateReport();
      });
      return;
    }

    CreateReport();
  };

  const LaunchReport = () => {
    if (!ValidSelectedReport()) return;

    if (isOpenReportDirty) {
      PromptToSave(() => {
        OpenReport(selectedReport);
      });
      return;
    }

    OpenReport(selectedReport);
  };

  const ValidSelectedReport = () => {
    return selectedReport != null && selectedReport.ID != null;
  };

  const handleReportRFC = async ({ mode, data }) => {
    if (!mode) return;
    const { result } = await DoReportRFC(mode, data);
    if (result["success"]) {
      if (result["reloadreports"] === true) {
        setReports(result["reports"]);
      }
    }
  };

  const checkNameInUse = newName => {
    const existingReportNames = reports.map(report => {
      return report.name;
    });
    return existingReportNames.includes(newName);
  };

  const validName = (newName, showAlert) => {
    const reason = GetNameValidation(newName ?? "");
    const wsMgr = GetWSMgr();

    if (reason !== enumNameValidation.valid) {
      if (showAlert) {
        var alert = new QAlert();
        alert.InitAlert(appstr("Rename failure"), GetNameValidationMessage(reason), null, wsMgr);
        return false;
      }
    }
    if (checkNameInUse(newName) === true) {
      if (showAlert) {
        var alert = new QAlert();
        alert.InitAlert(appstr("Rename failure"), appstr("nameinuse"), null, wsMgr);
      }
      return false;
    }
    return true;
  };

  const handleRename = (newName, eventInfo) => {
    const targetName = newName.trim();
    if (!validName(targetName, true)) {
      return;
    }
    const updateStruct = { data: { name: targetName }, reportID: eventInfo.ID };
    handleReportAction(updateStruct, "updatereport");

    if (currentOpenReport?.ID === eventInfo.ID) {
      UpdateOpenReport({ name: targetName }, false);
    }
  };

  const handleReportAction = (actionData, mode) => {
    let reportID = actionData?.reportID;
    let reportData = actionData?.data;
    if (reportID === null) return;
    let data = {};
    data["reportID"] = reportID;
    switch (mode) {
      case "updatereport":
        data["updateData"] = reportData;
        data["reloadreports"] = true;
        break;
      case "duplicatereport":
        const namesList = reports.map(report => {
          return report.name;
        });
        const newName = namesList.GetUniqueDuplicateName(reportData?.name);
        data["newReportName"] = newName;
        break;
      case "deletereport":
        const lastReportID = Globals._svrChartMgr.GetLastReportID();
        if (lastReportID === reportID) {
          Globals._svrChartMgr.SetLastReportID(undefined);
        }
        break;
      default:
        break;
    }
    handleReportRFC({ mode, data });
  };

  const handleReportListClick = (event, data) => {
    switch (event.detail) {
      case 1:
        setSelectedReport(data);
        break;
      case 2:
        setSelectedReport(data);
        LaunchReport();
        break;
      default:
    }
  };

  const handleSearchInput = value => {
    setSearchInput(value);
  };

  const toggleFilter = option => {
    let toggleChartID = option.value;
    let newFilterIds = [...filterChartIds];
    let idx = newFilterIds.indexOf(toggleChartID);
    if (idx > -1) newFilterIds.splice(idx, 1);
    else newFilterIds.push(toggleChartID);
    setFilterChartIds(newFilterIds);
  };

  const BuildChartFilterOptions = () => {
    const wsMgr = GetWSMgr();
    const charts = wsMgr.GetAllowedCharts();
    return charts.map(chart => {
      return { label: chart.name, value: chart.ID, clickHandler: toggleFilter };
    });
  };

  const hasReports = reports?.length > 0;

  const tableProps = {
    rowSize: "largeR",
    textSize: "mediumT"
  };

  let badgeProps = {
    showBadge: filterChartIds?.length > 0,
    border: false,
    color: "warning",
    size: "small",
    pin: "top-right",
    badgeContent: filterChartIds?.length
  };

  const filterTrigger = (
    <Badge {...badgeProps}>
      <ToolIcon
        tooltipProps={{ message: appstr("Reports filter") }}
        name={"filter"}
        size={"medium"}
      />
    </Badge>
  );

  return (
    <CentralizedModalContainer show={showReportExplorer}>
      <Modal
        {...props}
        id="report-explorer"
        show={showReportExplorer}
        centerScreen={true}
        onClose={closeReportExplorer}
        fullScreen={true}
        style={{ padding: 0 }}
      >
        <DialogTitle divider={true} onClose={closeReportExplorer}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"compass"} />
            {appstr("Report explorer")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"ocn-chart-explorer-content"}>
          <div className={"ocn-report-explorer"}>
            <Flex justifyContent={"space-between"} padding={"12px 24px"} width={"100%"}>
              <SectionPrompt
                title={appstr("Reports")}
                description={appstr(
                  "Create new reports of your organization, or run saved reports"
                )}
              />
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"16px"}
                width={"1000px"}
              >
                <Search
                  clearable
                  inputProps={{
                    disabled: !hasReports,
                    style: { fontSize: "14px" },
                    spellCheck: false
                  }}
                  size={"medium"}
                  onInput={handleSearchInput}
                  placeholder={appstr("Find report")}
                  timeout={200}
                  onResetInput={handleSearchInput}
                />
                <Menu
                  openCtrlRef={openCtrlRef}
                  trigger={filterTrigger}
                  listHeader={{ name: appstr("Filter by chart") }}
                  value={filterChartIds}
                  options={BuildChartFilterOptions()}
                  dataTestId={"filter-reports"}
                  stayOpenOnOptionSelect={true}
                  listWidth={"medium"}
                  position="bottom-start"
                />
                <div style={{ marginLeft: "32px" }}>
                  <Button
                    dataTestId={"report-explorer-action-create-report"}
                    modifier={"secondary"}
                    className={"oc-special-secondary"}
                    onClick={HandleCreateReport}
                  >
                    {appstr("Create report")}
                  </Button>
                </div>
              </Flex>
            </Flex>
            <div
              className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}
              style={{ height: "100%" }}
            >
              {hasReports ? (
                <ReportsTable
                  reports={reports}
                  handleReportClick={handleReportListClick}
                  handleReportAction={handleReportAction}
                  handleRename={handleRename}
                  chartIdFilter={filterChartIds}
                  inputFilterKey={searchInput}
                  selectedReport={selectedReport}
                  tableProps={tableProps}
                />
              ) : (
                <DialogTextContent modifier={"medium"} size={"xsmall"} type={"display"}>
                  {GetEmptyContent()}
                </DialogTextContent>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary onClick={closeReportExplorer}>
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={LaunchReport}
            dataTestId={"open-report-btn"}
            disabled={!ValidSelectedReport()}
          >
            {appstr("Run")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

const GetEmptyContent = padding => {
  return (
    <div style={{ padding: parseInt(padding) }}>
      <DialogTextContent size={"small"} colorModifier={"lightgrey"} type={"body"}>
        {appstr("No files or folders here")}
      </DialogTextContent>
    </div>
  );
};

export default ReportExplorer;
